import { useMemo } from 'react'

import type { ToolbarNoteListItem } from '#/shared/api/generated/shipment/models'
import { useListItemsApiV1ToolbarTypeGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { formatNoteTimestamp } from '#/shared/lib/format'

const NOTE_FEED_SIZE = 10

export interface ShipmentNoteFeedItem {
  id: string
  text: string
  timestamp: string
  occurredAt: string
  author: string | undefined
  rating: number | undefined
  isApp: boolean
}

interface UseShipmentNoteFeedOptions {
  enabled?: boolean
}

export interface UseShipmentNoteFeedResult {
  notes: ShipmentNoteFeedItem[]
  isLoading: boolean
}

function formatAuthor(user: ToolbarNoteListItem['user']): string | undefined {
  if (!user) return undefined
  const name = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return name.length > 0 ? name : undefined
}

export function toShipmentNoteFeedItem(
  note: ToolbarNoteListItem,
): ShipmentNoteFeedItem {
  return {
    id: note.id,
    text: note.text,
    timestamp: formatNoteTimestamp(note.created_at),
    occurredAt: note.created_at,
    author: formatAuthor(note.user),
    rating: note.rating ?? undefined,
    isApp: note.is_app ?? false,
  }
}

export function useShipmentNoteFeed(
  shipmentId: string,
  { enabled = true }: UseShipmentNoteFeedOptions = {},
): UseShipmentNoteFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'note',
    { shipment_id: shipmentId, size: NOTE_FEED_SIZE },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const notes = useMemo<ShipmentNoteFeedItem[]>(() => {
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarNoteListItem[]
    return items.map(toShipmentNoteFeedItem)
  }, [query.data])

  return {
    notes,
    isLoading: query.isLoading && enabled && Boolean(shipmentId),
  }
}
